import {
  Configuration,
  FrontendApi,
  FrontendApiUpdateSettingsFlowRequest,
  LoginFlow,
  LogoutFlow,
  RecoveryFlow,
  RegistrationFlow,
  Session,
  SettingsFlow,
  UiContainer,
  UiNode,
  VerificationFlow,
} from "@ory/client";
import { isAxiosError } from "axios";

export class OryAuthService {
  public readonly ory: FrontendApi;

  constructor() {
    this.ory = new FrontendApi(
      new Configuration({
        basePath: import.meta.env.VITE_ORY_BASE_URL,
        baseOptions: {
          withCredentials: true,
          credentials: "include",
        },
      })
    );
  }

  async getSession(): Promise<Session> {
    return (await this.ory.toSession()).data;
  }

  async login(values: { email: string; password: string; refresh: boolean }): Promise<LoginResult> {
    try {
      const loginFlow = await this.createBrowserLoginFlow(values.refresh);

      await this.ory.updateLoginFlow({
        flow: loginFlow.id,
        updateLoginFlowBody: {
          csrf_token: getCsrfValue(loginFlow.ui.nodes),
          identifier: values.email,
          password: values.password,
          method: "password",
        },
      });

      return "Success";
    } catch (e) {
      if (errorContainsId(e, ORY_ERR_INVALID_CREDENTIALS, ORY_TEXT_INVALID_CREDENTIALS)) {
        return "NotAuthorized";
      }

      return "Failure";
    }
  }
  async signup(values: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    roles: string[];
  }): Promise<SignupResult> {
    try {
      const registrationFlow = await this.createBrowserRegistrationFlow();

      await this.ory.updateRegistrationFlow({
        flow: registrationFlow.id,
        updateRegistrationFlowBody: {
          csrf_token: getCsrfValue(registrationFlow.ui.nodes),
          password: values.password,
          method: "password",
          traits: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            roles: values.roles,
          },
        },
      });
      return "Success";
    } catch (error) {
      if (errorContainsId(error, ORY_ERR_IDENTIFIER_ALREADY_EXISTS, ORY_TEXT_IDENTIFIER_ALREADY_EXISTS)) {
        return "EmailAlreadyTaken";
      } else if (errorContainsId(error, ORY_ERR_PASSWORD_BREACHED, ORY_TEXT_PASSWORD_BREACHED)) {
        return "PasswordBreached";
      } else if (
        errorContainsId(error, ORY_ERR_PASSWORD_TOO_SIMILAR_TO_IDENTIFIER, ORY_TEXT_PASSWORD_TOO_SIMILAR_TO_IDENTIFIER)
      ) {
        return "SimilarToIdentifier";
      } else if (errorContainsId(error, ORY_ERR_PASSWORD_POLICY_VIOLATION, ORY_TEXT_PASSWORD_POLICY_VIOLATION)) {
        return "WeakPassword";
      }
      return "Failure";
    }
  }

  async signupWithAzureAD(returnTo?: string): Promise<boolean> {
    try {
      const registrationFlow = await this.createRegistrationFlowEndingAnyOpenSession(returnTo);

      await this.ory.updateRegistrationFlow({
        flow: registrationFlow.id,
        updateRegistrationFlowBody: {
          csrf_token: getCsrfValue(registrationFlow.ui.nodes),
          method: "oidc",
          provider: "microsoft",
        },
      });

      return true;
    } catch (e) {
      if (isAxiosError(e)) {
        if (e.response?.status === 422) {
          const redirectUrl = `${e.response.data.redirect_browser_to}`;

          window.location.replace(redirectUrl);
          return true;
        }
      }
      return false;
    }
  }

  private async createRegistrationFlowEndingAnyOpenSession(returnTo?: string): Promise<RegistrationFlow> {
    try {
      return (await this.ory.createBrowserRegistrationFlow({ returnTo })).data;
    } catch (e) {
      if (!isSessionAlreadyAvailable(e) || !(await this.logout())) throw e;
      return (await this.ory.createBrowserRegistrationFlow({ returnTo })).data;
    }
  }

  async createBrowserRegistrationFlow(returnTo?: string): Promise<RegistrationFlow> {
    try {
      const result = await this.ory.createBrowserRegistrationFlow({ returnTo });
      return result.data;
    } catch (err) {
      throw Error("ory.createBrowserRegistrationFlow failed");
    }
  }

  async createBrowserLoginFlow(refresh: boolean): Promise<LoginFlow> {
    try {
      const result = await this.ory.createBrowserLoginFlow({
        refresh: refresh,
      });
      return result.data;
    } catch (err) {
      throw Error("ory.createBrowserLoginFlow failed");
    }
  }

  async createBrowserLogoutFlow(): Promise<LogoutFlow> {
    try {
      const result = await this.ory.createBrowserLogoutFlow();
      return result.data;
    } catch (err) {
      throw Error("ory.createBrowserLogoutFlow failed");
    }
  }

  async createBrowserSettingsFlow(): Promise<SettingsFlow> {
    try {
      const result = await this.ory.createBrowserSettingsFlow();
      return result.data;
    } catch (err) {
      throw Error("ory.createBrowserSettingsFlow failed");
    }
  }

  async createBrowserRecoveryFlow(): Promise<RecoveryFlow> {
    try {
      const result = await this.ory.createBrowserRecoveryFlow();
      return result.data;
    } catch (err) {
      throw Error("ory.createBrowserLogoutFlow failed");
    }
  }

  async createBrowserVerificationFlow(): Promise<VerificationFlow> {
    try {
      const result = await this.ory.createBrowserVerificationFlow();
      return result.data;
    } catch (err) {
      throw Error("ory.createBrowserLogoutFlow failed");
    }
  }

  async logout(): Promise<boolean> {
    try {
      const logoutFlow = await this.createBrowserLogoutFlow();
      await this.ory.updateLogoutFlow({
        token: logoutFlow.logout_token,
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  async sendVerificationLink(email: string): Promise<VerificationFlow | undefined> {
    try {
      const verificationFlow = await this.createBrowserVerificationFlow();

      const result = await this.ory.updateVerificationFlow({
        flow: verificationFlow.id,
        updateVerificationFlowBody: {
          email: email,
          csrf_token: getCsrfValue(verificationFlow.ui.nodes),
          method: "code",
        },
      });

      if (responseContainsErrorMessage(result.data.ui)) {
        return undefined;
      }

      return verificationFlow;
    } catch (err) {
      return undefined;
    }
  }

  async updateTraits(props: {
    firstName?: string;
    lastName?: string;
    email: string;
  }): Promise<"success" | "reauth" | "failure"> {
    try {
      const settingsFlow = await this.createBrowserSettingsFlow();

      const result = await this.ory.updateSettingsFlow({
        flow: settingsFlow.id,
        updateSettingsFlowBody: {
          csrf_token: getCsrfValue(settingsFlow.ui.nodes),
          method: "profile",
          traits: {
            firstName: props.firstName,
            lastName: props.lastName,
            email: props.email,
          },
        },
      });

      if (responseContainsErrorMessage(result.data.ui)) {
        return "failure";
      }

      return "success";
    } catch (err) {
      return "reauth";
    }
  }

  async provideEmailCode(values: { code: string; flowId: string }): Promise<boolean> {
    try {
      const flow = await this.ory.getVerificationFlow({
        id: values.flowId,
      });

      const result = await this.ory.updateVerificationFlow({
        flow: flow.data.id,
        updateVerificationFlowBody: {
          csrf_token: getCsrfValue(flow.data.ui.nodes),
          method: "code",
          code: values.code,
        },
      });
      if (responseContainsErrorMessage(result.data.ui)) {
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  }

  async initForgottenPasswordFlow(email: string, recoveryFlow: RecoveryFlow): Promise<InitForgottenPasswordFlowResult> {
    try {
      await this.ory.updateRecoveryFlow({
        flow: recoveryFlow.id,
        updateRecoveryFlowBody: {
          csrf_token: getCsrfValue(recoveryFlow.ui.nodes),
          email: email,
          recovery_address: email,
          method: "code",
        },
      });

      return "Success";
    } catch (e) {
      return "Failure";
    }
  }

  async getPrivilegedRecoverySession(props: { code: string; recoveryFlow: RecoveryFlow }): Promise<boolean> {
    try {
      const result = await this.ory.updateRecoveryFlow({
        flow: props.recoveryFlow.id,
        updateRecoveryFlowBody: {
          csrf_token: getCsrfValue(props.recoveryFlow.ui.nodes),
          method: "code",
          code: props.code,
        },
      });

      if (responseContainsErrorMessage(result.data.ui)) {
        return false;
      } else {
        return true;
      }
    } catch (e) {
      if (isAxiosError(e)) {
        if (e.response?.status === 422) {
          return true;
        }
      }
      return false;
    }
  }

  async changePassword(props: { email: string; newPassword: string }): Promise<ChangePasswordResult> {
    try {
      const updateSettingsFlow = await this.ory.createBrowserSettingsFlow();

      await this.ory.updateSettingsFlow({
        flow: updateSettingsFlow.data.id,
        updateSettingsFlowBody: {
          csrf_token: getCsrfValue(updateSettingsFlow.data.ui.nodes),
          password: props.newPassword,
          traits: {
            email: props.email,
          },
          method: "password",
        },
      } as FrontendApiUpdateSettingsFlowRequest);

      return "Success";
    } catch (e) {
      if (errorContainsId(e, ORY_ERR_PASSWORD_BREACHED, ORY_TEXT_PASSWORD_BREACHED)) {
        return "PasswordBreached";
      } else if (
        errorContainsId(e, ORY_ERR_PASSWORD_TOO_SIMILAR_TO_IDENTIFIER, ORY_TEXT_PASSWORD_TOO_SIMILAR_TO_IDENTIFIER)
      ) {
        return "SimilarToIdentifier";
      } else if (errorContainsId(e, ORY_ERR_PASSWORD_POLICY_VIOLATION, ORY_TEXT_PASSWORD_POLICY_VIOLATION)) {
        return "WeakPassword";
      } else {
        return "Failure";
      }
    }
  }
}

export interface CsrfNode {
  attributes: {
    name: string;
    value: string;
  };
}

export const getCsrfValue = (nodes: UiNode[]): string => {
  const csrfNode = nodes.filter((e) => {
    const node = e as CsrfNode;
    return node.attributes.name === "csrf_token";
  })[0] as CsrfNode;

  return csrfNode.attributes.value;
};

// Ory Kratos error message IDs. Canonical source:
//   https://github.com/ory/kratos/blob/master/text/id.go
//   https://raw.githubusercontent.com/ory/docs/master/docs/kratos/messages.json
// Our deployed environments use Ory Network (managed), which tracks master.
// Matching on ID is the primary path; the English text is a safety net for
// version skew / self-hosted builds where IDs may differ.
//
// Duplicate-credential signups produce one of two IDs depending on whether
// Kratos resolved a hint via credentials_identifier_similarity_check, so we
// match both.
const ORY_ERR_IDENTIFIER_ALREADY_EXISTS = [4000007, 4000028];
const ORY_ERR_INVALID_CREDENTIALS = 4000006;
const ORY_ERR_PASSWORD_POLICY_VIOLATION = 4000005;
const ORY_ERR_PASSWORD_TOO_SIMILAR_TO_IDENTIFIER = 4000031;
const ORY_ERR_PASSWORD_BREACHED = 4000034;

const ORY_TEXT_IDENTIFIER_ALREADY_EXISTS = "exists already";
const ORY_TEXT_INVALID_CREDENTIALS = "The provided credentials are invalid";
const ORY_TEXT_PASSWORD_BREACHED = "The password has been found in data breaches";
const ORY_TEXT_PASSWORD_TOO_SIMILAR_TO_IDENTIFIER =
  "The password can not be used because it is too similar to the identifier";
const ORY_TEXT_PASSWORD_POLICY_VIOLATION = "The password can not be used";

interface OryAuthError {
  id: string;
  ui: UiContainer;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOryAuthError = (e: any): e is OryAuthError => {
  return !!e?.ui?.nodes;
};

const errorContainsId = (e: unknown, idOrIds: number | number[], fallbackText?: string): boolean => {
  if (isAxiosError(e)) {
    const data = e.response?.data;
    if (isOryAuthError(data)) {
      const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
      if (ids.some((id) => responseContainsId(data.ui, id))) return true;
      if (fallbackText && responseContainsText(data.ui, fallbackText)) return true;
    }
  }
  return false;
};

const isSessionAlreadyAvailable = (e: unknown): boolean =>
  isAxiosError(e) && e.response?.status === 400 && e.response.data?.error?.id === "session_already_available";

const responseContainsId = (ui: UiContainer, id: number): boolean => {
  const genericIds = ui.messages?.map((m) => m.id) ?? [];
  const inputIds = ui.nodes.flatMap((n) => n.messages?.map((m) => m.id) ?? []);
  return [...genericIds, ...inputIds].includes(id);
};

const responseContainsText = (ui: UiContainer, text: string): boolean => {
  const generic = ui.messages?.map((m) => m.text) ?? [];
  const inputs = ui.nodes.flatMap((n) => n.messages?.map((m) => m.text) ?? []);
  return [...generic, ...inputs].some((t) => t?.includes(text));
};

const responseContainsErrorMessage = (ui: UiContainer): boolean => {
  const errorMessages = ui.messages?.filter((e) => e.type === "error") ?? [];
  return errorMessages.length > 0;
};

export type LoginResult = "Success" | "UserDoesNotExist" | "NotAuthorized" | "Failure";

export type SignupResult =
  | "Success"
  | "EmailAlreadyTaken"
  | "WeakPassword"
  | "PasswordBreached"
  | "SimilarToIdentifier"
  | "Failure";

export type ProvideEmailCodeResult = "Success" | "WrongCode" | "ExpiredCode" | "Failure";

export type InitForgottenPasswordFlowResult = "Success" | "UserNotFound" | "Failure";

export type ChangePasswordResult = "Success" | "Failure" | "WeakPassword" | "PasswordBreached" | "SimilarToIdentifier";
